import { SUPABASE_URL, SUPABASE_KEY } from './config';

// ── Helpers ──────────────────────────────────────────────────────────────────
const headers = () => ({
  'Content-Type': 'application/json',
  apikey: SUPABASE_KEY,
  Authorization: `Bearer ${SUPABASE_KEY}`,
});

// ── Session ID (persists for the browser tab session) ────────────────────────
function getSessionId() {
  let sid = sessionStorage.getItem('_ukr_sid');
  if (!sid) {
    sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem('_ukr_sid', sid);
  }
  return sid;
}

// ── Device / Browser detection ───────────────────────────────────────────────
function getDeviceType() {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
  if (/mobile|iphone|ipod|android|blackberry|mini|windows\sce|palm/i.test(ua)) return 'mobile';
  return 'desktop';
}

function getBrowser() {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  return 'Other';
}

// ── Geo lookup (free, no key, no rate limit, CORS-safe) ──────────────────────
async function getGeo() {
  try {
    const res = await fetch('https://get.geojs.io/v1/ip/geo.json');
    if (!res.ok) return {};
    const d = await res.json();
    const lat = parseFloat(d.latitude);
    const lng = parseFloat(d.longitude);
    return {
      country:      d.country      || null,
      country_code: d.country_code || null,
      city:         d.city         || null,
      lat: isNaN(lat) ? null : lat,
      lng: isNaN(lng) ? null : lng,
    };
  } catch {
    return {};
  }
}

// ── Track a pageview ─────────────────────────────────────────────────────────
export async function trackPageview(page = window.location.pathname) {
  if (!SUPABASE_URL || !SUPABASE_KEY) return;

  const geo = await getGeo();

  const payload = {
    session_id: getSessionId(),
    page,
    referrer: document.referrer || null,
    device_type: getDeviceType(),
    browser: getBrowser(),
    ...geo,
  };

  await fetch(`${SUPABASE_URL}/rest/v1/pageviews`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(payload),
  });
}

// ── Track a custom event ─────────────────────────────────────────────────────
export async function trackEvent(event_name, metadata = {}) {
  if (!SUPABASE_URL || !SUPABASE_KEY) return;

  await fetch(`${SUPABASE_URL}/rest/v1/events`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({
      session_id: getSessionId(),
      event_name,
      metadata,
    }),
  });
}

// ── Admin: fetch all rows ────────────────────────────────────────────────────
async function fetchAll(table) {
  if (!SUPABASE_URL || !SUPABASE_KEY) return [];
  const rows = [];
  let offset = 0;
  const limit = 1000;
  while (true) {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/${table}?select=*&order=created_at.desc&limit=${limit}&offset=${offset}`,
      { headers: headers() }
    );
    if (!res.ok) throw new Error(`Supabase error ${res.status}`);
    const batch = await res.json();
    rows.push(...batch);
    if (batch.length < limit) break;
    offset += limit;
  }
  return rows;
}

export const fetchAllPageviews = () => fetchAll('pageviews');
export const fetchAllEvents    = () => fetchAll('events');